/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Sparkles,
  Calculator,
  MessageSquare,
  Ruler,
  BookOpen,
  BarChart3,
  Receipt,
  Layers,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "AI Built for Electrical Contractors",
  title: "AI for electrical contractors",
  titleHighlight: "that keeps bids on code.",
  description: `One platform where AI helps electrical contractors bid faster, match costs to real supplier rates, audit scope before proposals ship, and run projects from one database. Built for 3–25 person teams. Grounded in your real business data — not generic construction averages.`,

  featureGrid: {
    title: "Where AI shows up for electrical contractors",
    items: [
      {
        icon: Sparkles,
        title: "Panel schedules",
        description:
          "Track panels, breakers, and circuits per job as structured line items.",
      },
      {
        icon: Sparkles,
        title: "Wire and conduit runs",
        description:
          "Draw wire runs and conduit on the drawing; AI matches each to your cost database.",
      },
      {
        icon: Sparkles,
        title: "Fixture schedule extraction",
        description:
          "Upload PDF fixture schedules and AI drafts structured line items for your review.",
      },
      {
        icon: Sparkles,
        title: "Service + new construction",
        description:
          "Residential service calls and commercial new construction in one workspace.",
      },
      {
        icon: Sparkles,
        title: "Labor code tracking",
        description:
          "Separate rates for commercial, residential, and industrial work.",
      },
      {
        icon: Sparkles,
        title: "Hank for electricians",
        description:
          "'What did we charge for a 200A service upgrade last month?' Real answer.",
      },
    ],
  },

  workflow: {
    title: "How it works for electrical contractors",
    description:
      "From first bid to final invoice — every step in one database, every AI decision reviewable.",
    steps: [
      {
        label: "Bid",
        title: "From drawings to priced estimate",
        description:
          "Upload panel and fixture schedules and AI drafts structured line items for review. Draw wire runs on the plans. AI matches each line to your cost database, and Hank audits the estimate for missing code items before you ship.",
        details: [],
      },
      {
        label: "Install",
        title: "Project tracking for electrical work",
        description:
          "Accepted bid becomes a project with task tracking per panel, rough-in, and trim phases. Budget vs. actual at every step.",
        details: [],
      },
      {
        label: "Service",
        title: "One workspace, any job size",
        description:
          "Service calls, retrofits, and commercial new construction all live in the same database. Customer history follows the customer, not the job type.",
        details: [],
      },
    ],
  },

  capabilities: [
    {
      icon: Sparkles,
      title: "Commercial, residential, and service in one system",
      description:
        "Electrical contractors often do all three — commercial buildouts, residential service, retrofit work. Contractor Co-Pilot handles all in one workspace with separate labor rates and cost databases per work type.",
      bullets: [
        "Unified workflow across commercial, residential, service",
        "Separate labor rates per work type",
        "Customer history across job types",
        "Crew assignment per project",
      ],
    },
    {
      icon: Sparkles,
      title: "AI drafts structured data from schedules",
      description:
        "Engineers ship panel and fixture schedules as PDF tables. Upload them and AI drafts structured line items for your review — skip the retyping, but you still review every row before it commits to the estimate.",
      bullets: [
        "PDF schedule extraction with human review",
        "Fixture count and type tracking",
        "Panel and circuit-level detail",
        "Engineer-readable structured output",
      ],
    },
    {
      icon: Sparkles,
      title: "AI audits missing code items",
      description:
        "Electrical jobs fail when a GFCI, AFCI, or grounding item is missed. Hank audits estimates against code-tagged items and flags anything that looks missing — before the inspector does.",
      bullets: [
        "Code item flagging per estimate",
        "Historical audit comparison",
        "Missing line detection",
        "Plain-English audit summaries",
      ],
    },
    {
      icon: Sparkles,
      title: "Labor variance for difficult access",
      description:
        "Running wire in a finished basement takes 2× the time of an open soffit. Variance factors adjust production rates per access condition so your bids don't lose money on hard jobs.",
      bullets: [
        "Access-based variance factors",
        "Production rate tracking from real jobs",
        "Per-job labor rate overrides",
        "Hank queries on historical labor data",
      ],
    },
  ],

  relatedFeatures: [
    {
      label: "Electrical Solutions",
      href: "/solutions/electrical",
      icon: Ruler,
    },
    {
      label: "Cost Calculator",
      href: "/tools/cost-calculator",
      icon: Calculator,
    },
    {
      label: "How to Estimate Electrical Work",
      href: "/blog/how-to-estimate-electrical-work",
      icon: BookOpen,
    },
    { label: "CC vs PlanSwift", href: "/compare/planswift", icon: BarChart3 },
    {
      label: "Construction Estimating",
      href: "/features/estimates",
      icon: Receipt,
    },
    { label: "Blueprint Takeoffs", href: "/features/takeoffs", icon: Layers },
    {
      label: "AI Assistant (Hank)",
      href: "/features/ai-assistant",
      icon: MessageSquare,
    },
    { label: "All Features", href: "/features", icon: Sparkles },
  ],
};

export function AiForElectricalContractorsPageClient() {
  return <FeaturePageTemplate config={config} />;
}
